import React, { useState, useEffect } from "react";
import FindCaseStepper from "../../components/crm/FindCaseStepper";
import { ArrowLeft, RefreshCw, Download, UploadCloud, FileText } from "lucide-react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import toast from "react-hot-toast";
import InfoModal from "../../components/InfoModal";
import { auth } from "../../firebase";
import { fetchHighCourtPdf } from "../../services/escrApi";
import { findExportedCaseForJudgment, attachJudgmentToCase } from "../../services/escrExport";

// Opens a single eSCR judgment as its own page (not a modal): the PDF plus a
// white "Export to Case" button that attaches the judgment to the matching
// already-exported case's Orders (see services/escrExport.js).
export default function ESCRJudgmentView() {
  const location = useLocation();
  const navigate = useNavigate();
  const row = location.state?.row || null;

  const [pdfBlobUrl, setPdfBlobUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [modal, setModal] = useState({ open: false, title: "", message: "" });

  useEffect(() => {
    if (!row) return;
    let cancelled = false;
    let createdUrl = "";
    (async () => {
      setLoading(true);
      try {
        const blob = await fetchHighCourtPdf(row);
        createdUrl = URL.createObjectURL(blob);
        if (!cancelled) setPdfBlobUrl(createdUrl);
      } catch (err) {
        if (!cancelled) toast.error(err.message || "Could not open judgment PDF");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [row]);

  const handleExport = async () => {
    const user = auth.currentUser;
    if (!user) return toast.error("Please log in to export.");
    if (!row) return;
    setExporting(true);
    try {
      const match = await findExportedCaseForJudgment(row, user.uid);
      if (!match) {
        setModal({
          open: true,
          title: "No Matching Case",
          message: `No exported case found matching ${row.caseNumber || "this judgment"} (by case number / CNR). Export that case first, then add this judgment to its Orders.`,
        });
        return;
      }
      const res = await attachJudgmentToCase({ judgment: row, match, ownerId: user.uid });
      if (!res.added && res.reason === "duplicate") {
        setModal({
          open: true,
          title: "Already Added",
          message: `This judgment is already in ${match.caseNo}'s Orders.`,
        });
      } else {
        setModal({
          open: true,
          title: "Exported",
          message: `Judgment added to ${match.caseNo} — it now appears in the Orders tab and in the Files "Orders" folder.`,
        });
      }
    } catch (e) {
      toast.error(e.message || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  if (!row) {
    return (
      <>
                <div className="relative min-h-screen flex flex-col items-center justify-center text-white">
          <div className="mb-4"><FindCaseStepper current={4} /></div>
          <p className="mb-4">No judgment selected.</p>
          <Link to="/portal/fetchfromescr" className="bg-yellow-500 text-[#060125] rounded py-2 px-6 font-bold">
            Go to eSCR Search
          </Link>
        </div>
      </>
    );
  }

  const Field = ({ label, value }) =>
    value ? (
      <div>
        <span className="text-slate-500">{label}: </span>
        <span className="text-white break-words">{value}</span>
      </div>
    ) : null;

  return (
    <>
                        <div className="relative min-h-screen flex flex-col">
          <div className="mb-4"><FindCaseStepper current={4} /></div>
        <main className="px-4 md:px-12 py-8">
          <div className="relative z-10 flex items-center justify-between gap-3 p-2 mb-4">
            <button onClick={() => navigate(-1)} className="flex items-center text-white min-w-0">
              <ArrowLeft className="shrink-0" />
              <span className="text-yellow-500 ml-3 font-bold text-xl md:text-2xl truncate">Judgment</span>
            </button>
            <div className="flex items-center gap-3 shrink-0">
              {pdfBlobUrl && (
                <a
                  href={pdfBlobUrl}
                  download={`${row.caseNumber || "judgment"}.pdf`}
                  className="hidden md:flex items-center gap-2 text-slate-500 hover:text-white text-sm"
                >
                  <Download className="h-4 w-4" /> Download
                </a>
              )}
              <button
                onClick={handleExport}
                disabled={exporting}
                className="flex items-center gap-2 bg-white text-black py-2 px-5 rounded-full font-bold hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <UploadCloud className="h-4 w-4" /> {exporting ? "Exporting…" : "Export to Case"}
              </button>
            </div>
          </div>

          <div className="relative z-10 max-w-5xl mx-auto">
            {/* Judgment metadata */}
            <div className="bg-white border border-slate-200 shadow-sm backdrop-blur-xl border border-white/15 rounded-2xl p-5 mb-4">
              <div className="flex items-start gap-2">
                <FileText className="h-5 w-5 text-emerald-300 shrink-0 mt-0.5" />
                <h2 className="text-slate-700 font-semibold text-lg leading-snug">
                  {[row.caseNumber, row.caseTitle].filter(Boolean).join(" — ")}
                </h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 mt-3 text-sm">
                <Field label="Case Number" value={row.caseNumber} />
                <Field label="CNR" value={row.cnr} />
                <Field label="Judge" value={row.judgeName} />
                <Field label="Court" value={row.courtName} />
                <Field label="Registration Date" value={row.registrationDate} />
                <Field label="Decision Date" value={row.decisionDate} />
                <Field
                  label="Disposal"
                  value={row.disposalNature && row.disposalNature !== "N/A" ? row.disposalNature : ""}
                />
              </div>
            </div>

            {/* PDF */}
            <div className="bg-white rounded-2xl overflow-hidden h-[80vh]">
              {loading ? (
                <div className="h-full flex items-center justify-center text-gray-500 gap-2">
                  <RefreshCw className="animate-spin h-5 w-5" /> Loading judgment…
                </div>
              ) : pdfBlobUrl ? (
                <iframe title="judgment-pdf" src={pdfBlobUrl} className="w-full h-full" />
              ) : (
                <div className="h-full flex items-center justify-center text-gray-500">
                  Judgment PDF is not available.
                </div>
              )}
            </div>
          </div>
        </main>
      </div>

      <InfoModal
        isOpen={modal.open}
        onClose={() => setModal((m) => ({ ...m, open: false }))}
        title={modal.title}
        message={modal.message}
      />
    </>
  );
}
